import React from "react";
import moment from "moment";
import clsx from "clsx";
import * as Icon from "react-feather";

import PageHeading from "../../common/page_heading/PageHeading";
import Tabs from "../../common/tabs/Tabs";
import Tab from "../../common/tabs/tab/Tab";
import BackofficeService from "../../../services/BackofficeService";
import { SERVICES, SERVICE_ORDER, ROLES, ROLE_ORDER, FREQUENCIES } from "../backofficeConstants";
import { I } from "../common/icons";
import { fmt } from "../backofficeUtils";
import {
	ENGAGEMENT_STATUSES,
	ENGAGEMENT_STATUS_ORDER,
	splitOpenCompleted,
	summarize,
	defaultRange,
	monthOptions,
	monthKey,
	reportByMonth,
	reportByService,
	reportByRole,
	emailRows,
	dueNote,
	groupTasks,
	taskDueNote,
	hours,
	pct,
	round,
} from "./backofficeEngagementUtils";

import "./backofficeEngagements.css";

const DATE = "YYYY-MM-DD";

const TASK_STATUS_CLS = { not_started: "notstarted", in_progress: "progress", pending_client: "pending", complete: "complete" };
const TASK_STATUS_LABEL = { not_started: "Not Started", in_progress: "In Progress", pending_client: "Pending Client", complete: "Complete" };

/**
 * The client page's Backoffice tab: the client's backoffice work shown as engagements, one per
 * service and period, for reporting. Read-only — tasks are created and worked on the Backoffice
 * page; this screen only reads them back rolled up (see /backoffice/engagement/list).
 */
class BackofficeEngagements extends React.Component {
	constructor(props) {
		super(props);
		const range = defaultRange();
		this.state = {
			loading: true,
			error: null,
			engagements: [],
			users: [],
			view: "list",
			fService: "",
			fStatus: "",
			fromMonth: monthKey(range.from),
			toMonth: monthKey(range.to),
			expanded: {},
		};
		this.months = monthOptions();
	}

	componentDidMount() {
		this.load();
	}

	componentDidUpdate(prevProps, prevState) {
		const { clientId } = this.props;
		const { fService, fStatus, fromMonth, toMonth } = this.state;
		if (prevProps.clientId !== clientId) {
			this.setState({ expanded: {} }, this.load);
			return;
		}
		if (prevState.fService !== fService || prevState.fStatus !== fStatus || prevState.fromMonth !== fromMonth || prevState.toMonth !== toMonth) {
			this.load();
		}
	}

	range() {
		const { fromMonth, toMonth } = this.state;
		const lo = fromMonth <= toMonth ? fromMonth : toMonth;
		const hi = fromMonth <= toMonth ? toMonth : fromMonth;
		return { from: moment(lo, "YYYY-MM").startOf("month").format(DATE), to: moment(hi, "YYYY-MM").endOf("month").format(DATE) };
	}

	load = async () => {
		const { clientId } = this.props;
		const { fService, fStatus } = this.state;
		if (!clientId) return;
		const { from, to } = this.range();
		this.setState({ loading: true, error: null });
		const [engagements, users] = await Promise.all([BackofficeService.listEngagements({ clientId, service: fService || undefined, status: fStatus || undefined, from, to }), BackofficeService.listCapacity()]);
		if (!engagements.ok) {
			this.setState({ loading: false, error: engagements.error || "Could not load backoffice engagements", engagements: [] });
			return;
		}
		this.setState({ loading: false, engagements: engagements.data || [], users: users.ok ? users.data : [] });
	};

	toggle = id => {
		this.setState(prev => {
			const expanded = Object.assign({}, prev.expanded);
			if (expanded[id]) delete expanded[id];
			else expanded[id] = true;
			return { expanded };
		});
	};

	user(id) {
		return this.state.users.find(u => u.id === id) || null;
	}

	// ------------------------------------------------------------------ small pieces

	renderAvatar(userId, key) {
		const u = this.user(userId);
		return (
			<span key={key} className={clsx("boe__av", { "boe__av--empty": !u })} title={u ? u.name : "Unassigned"}>
				{u ? u.initials || "?" : "–"}
			</span>
		);
	}

	renderTeam(engagement) {
		return (
			<div className="boe__team">
				{ROLE_ORDER.map(r => {
					const ids = engagement[`${r}UserIds`] || [];
					return (
						<span key={r} className="boe__role" title={`${ROLES[r].label}: ${ids.map(id => (this.user(id) || {}).name || "?").join(", ") || "Unassigned"}`}>
							{ids.length ? this.renderAvatar(ids[0], "a") : this.renderAvatar(null, "a")}
							{ids.length > 1 && <span className="boe__av boe__av--more">+{ids.length - 1}</span>}
							<small>{ROLES[r].short}</small>
						</span>
					);
				})}
			</div>
		);
	}

	renderStatus(status) {
		const s = ENGAGEMENT_STATUSES[status] || ENGAGEMENT_STATUSES.not_started;
		return (
			<span className={clsx("boe__status", `boe__status--${s.cls}`)}>
				<i />
				{s.label}
			</span>
		);
	}

	renderTaskStatus(task, today) {
		const overdue = task.status !== "complete" && task.dueDate && task.dueDate < today;
		return (
			<span className={clsx("boe__status", overdue ? "boe__status--overdue" : `boe__status--${TASK_STATUS_CLS[task.status] || "notstarted"}`)}>
				<i />
				{TASK_STATUS_LABEL[task.status] || task.status}
			</span>
		);
	}

	// ------------------------------------------------------------------ header

	renderHeader() {
		const { headerLeft } = this.props;
		const { view, fService, fStatus, fromMonth, toMonth } = this.state;
		return (
			<div className="boe__header">
				{headerLeft && <div className="boe__header__left">{headerLeft}</div>}
				<div className="boe__filters">
					<Tabs className="ft-boe-view" onSelect={t => this.setState({ view: t.id })} selected={view}>
						<Tab id={"list"} icon={<Icon.List size={16} strokeWidth={1.5} />} text="Engagements" />
						<Tab id={"report"} icon={<Icon.BarChart2 size={16} strokeWidth={1.5} />} text="Report" />
					</Tabs>
					<div className="boe__filter">
						<label htmlFor="boeService">Service</label>
						<select id="boeService" className="ft-boe-service" value={fService} onChange={e => this.setState({ fService: e.target.value })}>
							<option value="">All</option>
							{SERVICE_ORDER.map(s => (
								<option key={s} value={s}>
									{SERVICES[s].label}
								</option>
							))}
						</select>
					</div>
					<div className="boe__filter">
						<label htmlFor="boeFrom">From</label>
						<select id="boeFrom" className="ft-boe-from" value={fromMonth} onChange={e => this.setState({ fromMonth: e.target.value })}>
							{this.months.map(m => (
								<option key={m.key} value={m.key}>
									{m.label}
								</option>
							))}
						</select>
					</div>
					<div className="boe__filter">
						<label htmlFor="boeTo">To</label>
						<select id="boeTo" className="ft-boe-to" value={toMonth} onChange={e => this.setState({ toMonth: e.target.value })}>
							{this.months.map(m => (
								<option key={m.key} value={m.key}>
									{m.label}
								</option>
							))}
						</select>
					</div>
					<div className="boe__filter">
						<label htmlFor="boeStatus">Status</label>
						<select id="boeStatus" className="ft-boe-status" value={fStatus} onChange={e => this.setState({ fStatus: e.target.value })}>
							<option value="">Any</option>
							{ENGAGEMENT_STATUS_ORDER.map(s => (
								<option key={s} value={s}>
									{ENGAGEMENT_STATUSES[s].label}
								</option>
							))}
						</select>
					</div>
				</div>
			</div>
		);
	}

	renderTiles(summary) {
		const { from, to } = this.range();
		const rangeLabel = `${moment(from, DATE).format("MMM YYYY")} – ${moment(to, DATE).format("MMM YYYY")}`;
		return (
			<div className="boe__tiles ft-boe-tiles">
				<div className="boe__tile">
					<h5>Engagements</h5>
					<div className="boe__tile__value">{summary.engagements}</div>
					<div className="boe__tile__sub">
						{rangeLabel} · {summary.open} open
					</div>
				</div>
				<div className="boe__tile">
					<h5>Tasks complete</h5>
					<div className="boe__tile__value">
						{summary.tasksDone} <small>/ {summary.tasksTotal}</small>
					</div>
					<div className="boe__tile__sub">{pct(summary.tasksDone, summary.tasksTotal)}% in range</div>
				</div>
				<div className="boe__tile">
					<h5>Hours logged vs budget</h5>
					<div className="boe__tile__value">
						{hours(summary.logged)} <small>/ {hours(summary.budget)}h</small>
					</div>
					<div className="boe__tile__sub">{summary.budget ? `${pct(summary.logged, summary.budget)}% of budget used` : "no budget recorded"}</div>
				</div>
				<div className="boe__tile">
					<h5>Completed on time</h5>
					<div className="boe__tile__value">
						{summary.onTimeDone} <small>/ {summary.onTimeTotal}</small>
					</div>
					<div className="boe__tile__sub">{summary.onTimeTotal ? `${summary.onTimeTotal - summary.onTimeDone} finished late` : "nothing completed yet"}</div>
				</div>
				<div className={clsx("boe__tile", { "boe__tile--alert": summary.overdueTasks > 0 })}>
					<h5>Overdue tasks</h5>
					<div className="boe__tile__value">{summary.overdueTasks}</div>
					<div className="boe__tile__sub">{summary.overdueEngagements ? `across ${summary.overdueEngagements} engagement${summary.overdueEngagements === 1 ? "" : "s"}` : "nothing past due"}</div>
				</div>
			</div>
		);
	}

	// ------------------------------------------------------------------ list view

	renderTasks(engagement, today) {
		const groups = groupTasks(engagement.tasks);
		const rows = [];
		let remaining = engagement.tasks.length;
		groups.forEach(group => {
			const GroupIcon = group.groupIcon && I[group.groupIcon] ? I[group.groupIcon] : null;
			const emails = group.tasks.filter(t => t.emailAutomation).length;
			rows.push(
				<div key={`g-${engagement.id}-${group.groupName || "none"}`} className="boe__tgroup">
					{GroupIcon ? <GroupIcon size={13} /> : <Icon.CornerDownRight size={13} />}
					{group.groupName || "Ungrouped"}
					<span>
						{group.tasks.length} task{group.tasks.length === 1 ? "" : "s"}
						{emails ? ` · ${emails} client email${emails === 1 ? "" : "s"}` : ""}
					</span>
				</div>,
			);
			group.tasks.forEach(task => {
				remaining--;
				const sent = task.emailAutomation ? (task.emailAutomation.sends || []).filter(s => s.status === "sent").length : 0;
				const accounts = task.accounts || [];
				const accountsDone = accounts.filter(a => a.isDone).length;
				const logged = (task.logged.preparer || 0) + (task.logged.reviewer || 0) + (task.logged.manager || 0);
				const budget = (task.budget.preparer || 0) + (task.budget.reviewer || 0) + (task.budget.manager || 0);
				const late = task.status !== "complete" && task.dueDate && task.dueDate < today;
				const assigned = ROLE_ORDER.map(r => task[ROLES[r].idField]).filter(Boolean);
				rows.push(
					<div key={`t-${task.id}`} className={clsx("boe__cols", "boe__task", "ft-boe-task", { "boe__task--last": remaining === 0 })} data-task={task.id}>
						<div className="boe__task__title">
							<Icon.CornerDownRight size={13} />
							<b className={clsx({ "boe__task__title--link": task.isClientLink })} title={task.title}>
								{task.title}
							</b>
							{task.hint && (
								<span className="boe__task__hint" title={task.hint}>
									{task.hint}
								</span>
							)}
							{task.frequency && task.frequency !== "monthly" && task.frequency !== "one_time" && <span className="boe__task__hint">{FREQUENCIES[task.frequency]}</span>}
							{task.emailAutomation && (
								<span className="boe__tag" title="Client email automation">
									<Icon.Mail size={11} /> {sent} sent
								</span>
							)}
							{accounts.length > 0 && <span className={clsx("boe__tag", accountsDone === accounts.length ? "boe__tag--acct" : "boe__tag--acct-open")}>{`${accountsDone} / ${accounts.length} accounts`}</span>}
							{task.googleTaskId && !task.googleTaskError && (
								<span className="boe__tag boe__tag--acct" title="In the preparer's Google Tasks">
									<Icon.Check size={11} /> Google Tasks
								</span>
							)}
							{task.googleTaskError && (
								<span className="boe__tag" title={task.googleTaskError}>
									<Icon.AlertCircle size={11} /> Google Tasks
								</span>
							)}
							{task.isClientLink && !task.emailAutomation && (
								<span className="boe__tag">
									<Icon.Link size={11} /> client link
								</span>
							)}
						</div>
						<div />
						<div className="boe__cell">
							{fmt(task.startDate)} → {fmt(task.dueDate)}
						</div>
						<div className={clsx("boe__cell", { "boe__cell--late": late })}>{taskDueNote(task, today)}</div>
						<div className={clsx("boe__cell", { "boe__cell--late": logged > budget })}>
							{hours(logged)} / {hours(budget)}h
						</div>
						<div className="boe__team">{assigned.length ? assigned.map((id, i) => this.renderAvatar(id, `${task.id}-${i}`)) : this.renderAvatar(null, "none")}</div>
						<div>{this.renderTaskStatus(task, today)}</div>
						<div className={clsx("boe__cell", { "boe__cell--late": late })}>{fmt(task.dueDate)}</div>
					</div>,
				);
			});
		});
		return rows;
	}

	renderRow(engagement, today) {
		const { expanded } = this.state;
		const open = !!expanded[engagement.id];
		const done = engagement.status === "complete";
		const subParts = [`${engagement.taskCount} task${engagement.taskCount === 1 ? "" : "s"}`];
		if (engagement.clientEmailCount) subParts.push(`${engagement.clientEmailCount} client email${engagement.clientEmailCount === 1 ? "" : "s"}`);
		if (engagement.overdueCount) subParts.push(`${engagement.overdueCount} overdue`);
		if (done && engagement.completedAt) subParts.push(`completed ${fmt(engagement.completedAt)}${engagement.onTime === false ? " · late" : engagement.onTime ? " · on time" : ""}`);
		else if (engagement.lastActivityAt) subParts.push(`last activity ${moment(engagement.lastActivityAt).format("MMM DD")}`);
		const loggedTotal = engagement.logged.total;
		const budgetTotal = engagement.budget.total;
		const over = budgetTotal > 0 && loggedTotal > budgetTotal;
		const openLate = !done && engagement.dueDate && engagement.dueDate < today;
		const color = `var(--boe-svc-${engagement.service})`;

		const row = (
			<div key={engagement.id} className={clsx("boe__cols", "boe__row", "ft-boe-engagement", { "boe__row--done": done })} data-engagement={engagement.id}>
				<div className="boe__name">
					<button className={clsx("boe__chev", "ft-boe-expand", { "boe__chev--open": open })} type="button" aria-expanded={open} aria-label={open ? "Hide tasks" : "Show tasks"} onClick={() => this.toggle(engagement.id)}>
						<Icon.ChevronRight size={15} />
					</button>
					<span className="boe__bar" style={{ "--c": color }} />
					<div style={{ minWidth: 0 }}>
						<b>{engagement.name}</b>
						<small>{subParts.join(" · ")}</small>
					</div>
				</div>
				<div>
					<span className={clsx("boe__svc", `boe__svc--${engagement.service}`)}>
						<i />
						{SERVICES[engagement.service] ? SERVICES[engagement.service].label.replace(" bookkeeping", "").replace("Accounts payable (AP)", "AP") : engagement.service}
					</span>
				</div>
				<div className="boe__cell">
					{engagement.periodLabel}
					<small>
						{fmt(engagement.periodStart)} – {fmt(engagement.periodEnd)}
					</small>
				</div>
				<div className="boe__cell">
					{engagement.completeCount} / {engagement.taskCount}
					<small>{pct(engagement.completeCount, engagement.taskCount)}%</small>
				</div>
				<div className={clsx("boe__hours", { "boe__hours--over": over })}>
					<div className="boe__hours__line">
						<span>
							{hours(loggedTotal)} / {hours(budgetTotal)}h
						</span>
						<span>{budgetTotal ? `${pct(loggedTotal, budgetTotal)}%` : "—"}</span>
					</div>
					<div className="boe__meter" style={{ "--c": over ? "#d92d20" : done ? "#2e9e5b" : color }}>
						<i style={{ width: `${budgetTotal ? Math.min(100, pct(loggedTotal, budgetTotal)) : 0}%` }} />
					</div>
				</div>
				{this.renderTeam(engagement)}
				<div>{this.renderStatus(engagement.status)}</div>
				<div className={clsx("boe__cell", { "boe__cell--late": openLate || engagement.onTime === false })}>
					{fmt(engagement.dueDate)}
					<small>{dueNote(engagement, today)}</small>
				</div>
			</div>
		);
		return open ? [row].concat(this.renderTasks(engagement, today)) : row;
	}

	renderList(engagements, summary, today) {
		const { open, completed } = splitOpenCompleted(engagements);
		const byRole = reportByRole(engagements);
		return (
			<div className="boe__table-wrap">
				<div className="boe__table">
					<div className="boe__cols boe__thead">
						<div>Name</div>
						<div>Service</div>
						<div>Period</div>
						<div>Tasks</div>
						<div>Hours</div>
						<div>P / R / M</div>
						<div>Status</div>
						<div>Due</div>
					</div>
					{open.length > 0 && (
						<div className="boe__group ft-boe-group-open">
							Open <span>· {open.length} engagement{open.length === 1 ? "" : "s"}</span>
						</div>
					)}
					{open.map(e => this.renderRow(e, today))}
					{completed.length > 0 && (
						<div className="boe__group ft-boe-group-completed">
							Completed <span>· {completed.length} engagement{completed.length === 1 ? "" : "s"} in range</span>
						</div>
					)}
					{completed.map(e => this.renderRow(e, today))}
					<div className="boe__cols boe__tfoot">
						<div>
							Totals
							<small>
								{summary.engagements} engagements · {summary.open} open · {summary.engagements - summary.open} complete
							</small>
						</div>
						<div />
						<div />
						<div>
							{summary.tasksDone} / {summary.tasksTotal}
						</div>
						<div>
							{hours(summary.logged)} / {hours(summary.budget)}h<small>{byRole.map(r => `${ROLES[r.role].short} ${hours(r.logged)}`).join(" · ")}</small>
						</div>
						<div />
						<div />
						<div />
					</div>
				</div>
			</div>
		);
	}

	// ------------------------------------------------------------------ report view

	renderReport(engagements) {
		const byMonth = reportByMonth(engagements);
		const byService = reportByService(engagements);
		const byRole = reportByRole(engagements);
		const emails = emailRows(engagements);
		const totals = byMonth.reduce(
			(acc, r) => {
				acc.engagements += r.engagements;
				acc.tasksDone += r.tasksDone;
				acc.tasksTotal += r.tasksTotal;
				acc.logged += r.logged;
				acc.budget += r.budget;
				acc.onTimeDone += r.onTimeDone;
				acc.onTimeTotal += r.onTimeTotal;
				SERVICE_ORDER.forEach(s => {
					acc.byService[s].logged += r.byService[s].logged;
					acc.byService[s].budget += r.byService[s].budget;
				});
				return acc;
			},
			{ engagements: 0, tasksDone: 0, tasksTotal: 0, logged: 0, budget: 0, onTimeDone: 0, onTimeTotal: 0, byService: SERVICE_ORDER.reduce((o, s) => Object.assign(o, { [s]: { logged: 0, budget: 0 } }), {}) },
		);
		const variance = v => (v === 0 ? <span>0h</span> : <span className={v < 0 ? "boe__pos" : "boe__neg"}>{`${v > 0 ? "+" : "−"}${hours(Math.abs(v))}h`}</span>);
		const onTime = (done, total, open) => {
			if (!total) return <span className="boe__pill boe__pill--muted">{open ? `${open} open` : "—"}</span>;
			return (
				<span className={clsx("boe__pill", { "boe__pill--late": done < total })}>
					{done} / {total}
				</span>
			);
		};
		const svcHours = cell => (cell.engagements ? `${hours(cell.logged)} / ${hours(cell.budget)}` : "—");

		return (
			<div className="ft-boe-report">
				<div className="boe__card">
					<h3>
						Hours by month and service <small>· logged / budget</small>
						<span className="boe__card__right">{totals.engagements} engagements</span>
					</h3>
					<div className="boe__rtable-wrap">
						{byMonth.length === 0 ? (
							<div className="boe__empty-cell">No backoffice engagements in this range.</div>
						) : (
							<table className="boe__rtable">
								<thead>
									<tr>
										<th>Month</th>
										<th>Engagements</th>
										<th>Tasks</th>
										{SERVICE_ORDER.map(s => (
											<th key={s}>{s === "monthend" ? "Month-end" : s === "ap" ? "AP" : SERVICES[s].label}</th>
										))}
										<th>Logged</th>
										<th>Budget</th>
										<th>Variance</th>
										<th>On time</th>
									</tr>
								</thead>
								<tbody>
									{byMonth.map(r => (
										<tr key={r.key}>
											<td className="boe__rtable__n">{r.label}</td>
											<td>{r.engagements}</td>
											<td>
												{r.tasksDone} / {r.tasksTotal}
											</td>
											{SERVICE_ORDER.map(s => (
												<td key={s} className={clsx({ boe__rtable__sub: !r.byService[s].engagements })}>
													{svcHours(r.byService[s])}
												</td>
											))}
											<td className="boe__rtable__n">{hours(r.logged)}</td>
											<td>{hours(r.budget)}</td>
											<td>{r.open && !r.onTimeTotal ? <span className="boe__rtable__sub">{r.open} open</span> : variance(r.variance)}</td>
											<td>{onTime(r.onTimeDone, r.onTimeTotal, r.open)}</td>
										</tr>
									))}
									<tr className="boe__rtable__total">
										<td>Total</td>
										<td>{totals.engagements}</td>
										<td>
											{totals.tasksDone} / {totals.tasksTotal}
										</td>
										{SERVICE_ORDER.map(s => (
											<td key={s}>{totals.byService[s].budget || totals.byService[s].logged ? `${hours(totals.byService[s].logged)} / ${hours(totals.byService[s].budget)}` : "—"}</td>
										))}
										<td>{hours(totals.logged)}</td>
										<td>{hours(totals.budget)}</td>
										<td>{variance(round(totals.logged - totals.budget))}</td>
										<td>{onTime(totals.onTimeDone, totals.onTimeTotal, 0)}</td>
									</tr>
								</tbody>
							</table>
						)}
					</div>
				</div>

				<div className="boe__report-row">
					<div className="boe__card">
						<h3>
							By service <small>· budget use</small>
						</h3>
						{byService.length === 0 ? (
							<div className="boe__empty-cell">Nothing in range.</div>
						) : (
							<table className="boe__rtable">
								<thead>
									<tr>
										<th>Service</th>
										<th>Engagements</th>
										<th>Tasks done</th>
										<th>Budget use</th>
										<th>Overdue</th>
									</tr>
								</thead>
								<tbody>
									{byService.map(r => (
										<tr key={r.service}>
											<td>
												<span className="boe__swatch" style={{ "--c": `var(--boe-svc-${r.service})` }}>
													<i />
													{r.label}
												</span>
											</td>
											<td>{r.engagements}</td>
											<td>
												{r.tasksDone} / {r.tasksTotal}
											</td>
											<td>
												<span className="boe__rbar" style={{ "--c": r.budgetUsePct > 100 ? "#d92d20" : `var(--boe-svc-${r.service})` }}>
													<i style={{ width: `${Math.min(100, r.budgetUsePct)}%` }} />
												</span>
												{r.budget ? `${r.budgetUsePct}%` : "—"}
											</td>
											<td className={clsx({ boe__neg: r.overdueTasks > 0, boe__rtable__sub: !r.overdueTasks })}>{r.overdueTasks}</td>
										</tr>
									))}
								</tbody>
							</table>
						)}
					</div>

					<div className="boe__card">
						<h3>
							Hours by role <small>· logged / budget</small>
						</h3>
						<table className="boe__rtable">
							<thead>
								<tr>
									<th>Role</th>
									<th>Logged</th>
									<th>Budget</th>
									<th>Share</th>
								</tr>
							</thead>
							<tbody>
								{byRole.map(r => (
									<tr key={r.role}>
										<td className="boe__rtable__n">{r.label}</td>
										<td>{hours(r.logged)}</td>
										<td>{hours(r.budget)}</td>
										<td>{r.share}%</td>
									</tr>
								))}
								<tr className="boe__rtable__total">
									<td>Total</td>
									<td>{hours(byRole.reduce((n, r) => n + r.logged, 0))}</td>
									<td>{hours(byRole.reduce((n, r) => n + r.budget, 0))}</td>
									<td>{byRole.some(r => r.logged) ? "100%" : "—"}</td>
								</tr>
							</tbody>
						</table>
						<div className="boe__stack" role="img" aria-label={byRole.map(r => `${r.label} ${r.share}%`).join(", ")}>
							<i style={{ width: `${byRole[0].share}%`, background: "#2e9e5b" }} />
							<i style={{ width: `${byRole[1].share}%`, background: "#5a7ad0" }} />
							<i style={{ width: `${byRole[2].share}%`, background: "#8a5cd6" }} />
						</div>
						<div className="boe__legend">
							<span>
								<i style={{ background: "#2e9e5b" }} />
								Preparer
							</span>
							<span>
								<i style={{ background: "#5a7ad0" }} />
								Reviewer
							</span>
							<span>
								<i style={{ background: "#8a5cd6" }} />
								Manager
							</span>
						</div>
					</div>
				</div>

				<div className="boe__card">
					<h3>
						Client email automations <small>· sends in range</small>
						<span className="boe__card__right">{emails.length} automation{emails.length === 1 ? "" : "s"}</span>
					</h3>
					<div className="boe__rtable-wrap">
						{emails.length === 0 ? (
							<div className="boe__empty-cell">No client email automations on these engagements.</div>
						) : (
							<table className="boe__rtable">
								<thead>
									<tr>
										<th>Engagement</th>
										<th>Task</th>
										<th>Scheduled</th>
										<th>Sent</th>
										<th>Skipped</th>
										<th>Failed</th>
										<th>First send</th>
										<th>Last sent</th>
										<th>Result</th>
									</tr>
								</thead>
								<tbody>
									{emails.map(r => (
										<tr key={r.key}>
											<td className="boe__rtable__n">{r.engagementName}</td>
											<td style={{ textAlign: "left" }}>{r.taskTitle}</td>
											<td>{r.scheduled}</td>
											<td>{r.sent}</td>
											<td>{r.skipped}</td>
											<td className={clsx({ boe__neg: r.failed > 0 })}>{r.failed}</td>
											<td>{r.firstSend ? fmt(r.firstSend) : "—"}</td>
											<td>{r.lastSent ? fmt(r.lastSent) : "—"}</td>
											<td>
												{r.taskComplete ? (
													<span className="boe__pill">Resolved{r.status === "completed" || r.status === "stopped" ? " · stopped" : ""}</span>
												) : r.status === "active" ? (
													<span className="boe__pill boe__pill--open">Active{r.nextSend ? ` · next ${fmt(r.nextSend)}` : ""}</span>
												) : (
													<span className="boe__pill boe__pill--muted">{r.status || "—"}</span>
												)}
											</td>
										</tr>
									))}
								</tbody>
							</table>
						)}
					</div>
				</div>
			</div>
		);
	}

	// ------------------------------------------------------------------ page

	render() {
		const { title } = this.props;
		const { loading, error, engagements, view } = this.state;
		const today = moment().format(DATE);
		const summary = summarize(engagements);

		return (
			<div className="boe ft-boe">
				<PageHeading section="Workflow" title={title || "Backoffice engagements"} />
				{this.renderHeader()}
				<div className="boe__intro">One engagement per service and period, built from this client's backoffice tasks. Read-only: tasks are created and worked on the Backoffice page.</div>

				{error && (
					<div className="boe__state boe__state--error ft-boe-error">
						<b>Could not load backoffice engagements</b>
						{error}
					</div>
				)}
				{!error && loading && <div className="boe__state ft-boe-loading">Loading backoffice engagements…</div>}
				{!error && !loading && engagements.length === 0 && (
					<div className="boe__state ft-boe-empty">
						<b>No backoffice engagements in this range</b>
						Engagements appear here once the client has backoffice tasks for a period. Tasks are added on the Backoffice page, by hand or from a template.
					</div>
				)}
				{!error && !loading && engagements.length > 0 && (
					<React.Fragment>
						{this.renderTiles(summary)}
						{view === "list" ? this.renderList(engagements, summary, today) : this.renderReport(engagements)}
						{view === "list" && (
							<div className="boe__footnote">
								<div>Status is derived from the tasks: any open task past its due date → Overdue; all tasks complete → Complete; otherwise the furthest-along open task.</div>
								<div>
									Showing {engagements.length} engagement{engagements.length === 1 ? "" : "s"}
								</div>
							</div>
						)}
					</React.Fragment>
				)}
			</div>
		);
	}
}

export default BackofficeEngagements;
